import { useMemo } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, LabelList, Line, LineChart,
  Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, MoveRight, HelpCircle } from "lucide-react";

/**
 * Phase 8 chart kit. Every chart is wrapped in ChartFrame which ALWAYS shows:
 *   - "Responses: X"
 *   - the mandatory voluntary-poll disclaimer
 * Charts remain readable without relying on colour: numeric labels are printed
 * directly on bars/points and legends use text, not colour alone.
 */

export const CHART_COLORS = [
  "hsl(var(--chart-1))", "hsl(var(--chart-2))", "hsl(var(--chart-3))",
  "hsl(var(--chart-4))", "hsl(var(--chart-5))",
];

const DISCLAIMER = "Voluntary community poll — not a scientific opinion poll.";

export const ChartFrame = ({ title, responses, children, footnote, testId }) => (
  <div data-testid={testId || "chart-frame"} className="w-full min-w-0">
    {title && <p className="font-heading font-semibold text-sm text-foreground mb-1">{title}</p>}
    <p className="text-xs font-semibold text-foreground mb-2" data-testid="chart-response-count">
      Responses: <span className="tabular-nums">{responses ?? "—"}</span>
    </p>
    <div className="w-full min-w-0 overflow-hidden">{children}</div>
    {footnote && <p className="text-[10px] text-muted-foreground mt-1.5">{footnote}</p>}
    <p className="text-[10px] text-muted-foreground mt-1.5 italic" data-testid="chart-disclaimer">
      {DISCLAIMER}
    </p>
  </div>
);

/** Trend arrow badge — direction text included so colour is never the only cue. */
export const TrendBadge = ({ trend, testId }) => {
  if (!trend) return null;
  const map = {
    rising: { icon: TrendingUp, cls: "bg-amber-50 text-amber-900 border-amber-300", label: trend.text || "Increasing concern" },
    falling: { icon: TrendingDown, cls: "bg-sky-50 text-sky-900 border-sky-200", label: trend.text || "Decreasing" },
    steady: { icon: MoveRight, cls: "bg-slate-50 text-slate-700 border-slate-200", label: trend.text || "Steady" },
    not_enough_data: { icon: HelpCircle, cls: "bg-slate-50 text-muted-foreground border-slate-200", label: "Not enough data yet" },
  };
  const m = map[trend.direction] || map.not_enough_data;
  const Icon = m.icon;
  return (
    <Badge variant="outline" className={`text-[10px] gap-1 ${m.cls}`} data-testid={testId || `trend-${trend.direction}`}>
      <Icon className="h-3 w-3" /> {trend.arrow} {m.label}
    </Badge>
  );
};

/** Horizontal ranking chart — best for Top Issues. Mobile-first (fluid width). */
export const RankingChart = ({ items, testId }) => {
  const data = useMemo(() => (items || []).map((i) => ({
    name: i.label, value: i.count, percent: i.percent,
  })), [items]);
  const h = Math.max(data.length * 42, 120);
  return (
    <div style={{ width: "100%", height: h }} data-testid={testId || "ranking-chart"}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout="vertical" margin={{ top: 4, right: 56, left: 4, bottom: 4 }}>
          <XAxis type="number" hide />
          <YAxis type="category" dataKey="name" width={128}
                 tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} axisLine={false} tickLine={false} />
          <Tooltip formatter={(v, _n, p) => [`${v} responses (${p.payload.percent}%)`, p.payload.name]}
                   cursor={{ fill: "hsl(var(--muted))" }} />
          <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={22}>
            {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            <LabelList dataKey="percent" position="right"
                       formatter={(v) => `${v}%`}
                       style={{ fontSize: 11, fill: "hsl(var(--foreground))", fontVariantNumeric: "tabular-nums" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Vertical bar chart — best for comparing answer choices in one poll. */
export const AnswerBarChart = ({ options, testId }) => {
  const data = useMemo(() => (options || []).map((o) => ({
    name: o.label, value: o.count, percent: o.percent,
  })), [options]);
  return (
    <div style={{ width: "100%", height: 220 }} data-testid={testId || "answer-bar-chart"}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 18, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
          <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0}
                 tickFormatter={(v) => (v.length > 12 ? `${v.slice(0, 11)}…` : v)} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
          <Tooltip formatter={(v, _n, p) => [`${v} responses (${p.payload.percent}%)`, p.payload.name]} />
          <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} maxBarSize={44}>
            <LabelList dataKey="value" position="top" style={{ fontSize: 11, fill: "hsl(var(--foreground))" }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Line chart — responses over time (weekly buckets). */
export const TrendLineChart = ({ series, testId }) => {
  const data = (series || []).map((s) => ({ week: s.week?.slice(5), responses: s.responses }));
  return (
    <div style={{ width: "100%", height: 180 }} data-testid={testId || "trend-line-chart"}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
          <XAxis dataKey="week" tick={{ fontSize: 10 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} />
          <Tooltip formatter={(v) => [`${v} responses`, "Week of"]} />
          <Line type="monotone" dataKey="responses" stroke={CHART_COLORS[1]} strokeWidth={2}
                dot={{ r: 3, fill: CHART_COLORS[1] }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

/** Grouped horizontal bars for the Compare Areas tool (text labels carry meaning). */
export const CompareChart = ({ rows, aLabel, bLabel, testId }) => {
  const data = (rows || []).map((r) => ({ name: r.label, A: r.a_percent, B: r.b_percent }));
  const h = Math.max(data.length * 52, 140);
  return (
    <div data-testid={testId || "compare-chart"}>
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-foreground mb-1.5">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: CHART_COLORS[0] }} /> A: {aLabel}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: CHART_COLORS[3] }} /> B: {bLabel}
        </span>
      </div>
      <div style={{ width: "100%", height: h }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ top: 4, right: 48, left: 4, bottom: 4 }}>
            <XAxis type="number" hide domain={[0, 100]} />
            <YAxis type="category" dataKey="name" width={120}
                   tick={{ fontSize: 11, fill: "hsl(var(--foreground))" }} axisLine={false} tickLine={false} />
            <Tooltip formatter={(v, n) => [`${v}%`, n === "A" ? aLabel : bLabel]} />
            <Bar dataKey="A" fill={CHART_COLORS[0]} radius={[0, 3, 3, 0]} maxBarSize={12}>
              <LabelList dataKey="A" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 10 }} />
            </Bar>
            <Bar dataKey="B" fill={CHART_COLORS[3]} radius={[0, 3, 3, 0]} maxBarSize={12}>
              <LabelList dataKey="B" position="right" formatter={(v) => `${v}%`} style={{ fontSize: 10 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

/** Donut — used sparingly, only when a share-of-whole genuinely helps. */
export const IssueDonut = ({ items, testId }) => {
  const data = (items || []).slice(0, 5).map((i) => ({ name: i.label, value: i.count, percent: i.percent }));
  return (
    <div className="flex flex-col sm:flex-row items-center gap-3" data-testid={testId || "issue-donut"}>
      <div style={{ width: 150, height: 150 }} className="shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" innerRadius={44} outerRadius={68} paddingAngle={2}>
              {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v, n, p) => [`${v} responses (${p.payload.percent}%)`, n]} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="text-xs space-y-1 min-w-0">
        {data.map((d, i) => (
          <li key={d.name} className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-sm shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
            <span className="truncate">{d.name}</span>
            <span className="tabular-nums text-muted-foreground shrink-0">{d.percent}% ({d.value})</span>
          </li>
        ))}
      </ul>
    </div>
  );
};
